import React from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { useNavigate } from 'react-router-dom';

import {
  useGetApiV5FirmsRequests,
  IndexRequestSchemaDataItemStatus,
  GetApiV5FirmsRequestsStatus,
} from '@liscio/api';
import {
  Alert,
  Box,
  DataGrid,
  GridColDef,
  GridRowParams,
  gridClasses,
  styled,
  Typography,
} from '@liscio/ui';

import { Header } from '../../../DashboardView/components/Header/Header';
import { generateRedirectUrl } from 'components/DesktopSiteRedirect/desktop-site-redirect-utils';
import { useHomeData } from 'fetch-utils/users/user-hooks';
import { AccountCell } from 'modules/requests/components/RequestsTableCells/AccountCell/AccountCell';
import { AssigneeCell } from 'modules/requests/components/RequestsTableCells/AssigneeCell/AssigneeCell';
import { DueDateCell } from 'modules/requests/components/RequestsTableCells/DueDateCell/DueDateCell';
import { LastActivityCell } from 'modules/requests/components/RequestsTableCells/LastActivityCell/LastActivityCell';
import {
  REQUESTS_INDEX,
  REQUESTS_PATHS,
} from 'modules/requests/route-constants';
import { CompletionCell } from 'modules/rfi-requests/components/RfiRequestsTableCells';

const REQUESTS_LIMIT = 5;

const MyRequestsDataGrid = styled(DataGrid)(({ theme }) => ({
  fontWeight: 400,
  letterSpacing: 'inherit',
  color: theme.palette.text.primary,
  borderRadius: '0px',
  border: 'none',
  [`& .${gridClasses.columnHeaders}`]: {
    [`& .${gridClasses.columnHeader}`]: {
      background: theme.palette.grey[100],
    },
  },
  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.cell}:focus`]: {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '-2px',
  },
  [`& .${gridClasses.columnHeaderTitle}`]: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  [`& .${gridClasses.row}`]: {
    cursor: 'pointer',
  },
  [`& .${gridClasses.columnSeparator}`]: {
    display: 'none',
  },
}));

const columns: GridColDef[] = [
  {
    field: 'title',
    headerName: 'Name',
    flex: 1.5,
    sortable: false,
  },
  {
    field: 'assigned_contacts',
    headerName: 'Assigned To',
    flex: 1,
    sortable: false,
    renderCell: ({ value }) => <AssigneeCell assignees={value} />,
  },
  {
    field: 'assigned_account',
    headerName: 'Account',
    flex: 1,
    sortable: false,
    renderCell: ({ value }) => <AccountCell account={value} />,
  },
  {
    field: 'due_date',
    headerName: 'Due Date',
    width: 150,
    sortable: false,
    renderCell: ({ value }) => value && <DueDateCell date={value} />,
  },
  {
    field: 'percent_complete',
    headerName: 'Progress',
    width: 86,
    align: 'center',
    sortable: false,
    renderCell: ({ value }) => (
      <CompletionCell completionPercentage={value ?? 0} />
    ),
  },
  {
    field: 'updated_at',
    headerName: 'Last Activity',
    width: 140,
    sortable: false,
    renderCell: ({ value }) => <LastActivityCell date={value} />,
  },
];

export const DashboardMyRequests: React.FC = () => {
  const navigate = useNavigate();
  const { requestRefactorFirmShow, requestRefactorDispatchShow } = useFlags();
  const { data: homeData } = useHomeData();
  const employeeUuid = homeData?.data?.employee_uuid;
  const firmType = homeData?.data?.firm_type?.trim().toLowerCase();
  const isLiscioLite = firmType === 'liscio-lite';

  const {
    data: requestData,
    isLoading,
    error,
    isSuccess,
  } = useGetApiV5FirmsRequests(
    employeeUuid && !isLiscioLite
      ? {
          owner_id: employeeUuid,
          status: 'open' as unknown as GetApiV5FirmsRequestsStatus,
          per_page: REQUESTS_LIMIT,
          sort: '-due_date',
        }
      : undefined,
    {
      query: {
        enabled: !!employeeUuid && !isLiscioLite,
      },
    }
  );

  const rows = requestData?.data ?? [];
  const totalCount = requestData?.meta?.total_entries ?? 0;

  const handleRowClick = (params: GridRowParams) => {
    if (
      params.row.status === IndexRequestSchemaDataItemStatus.draft &&
      requestRefactorDispatchShow
    ) {
      navigate(`/requests/dispatch/${params.row.uuid}`);
    } else if (params.row.status === IndexRequestSchemaDataItemStatus.draft) {
      window.location.href = generateRedirectUrl(
        `/requests/dispatch/${params.row.uuid}`
      );
    } else if (requestRefactorFirmShow) {
      navigate(`/requests/${params.row.uuid}`);
    } else {
      window.location.href = generateRedirectUrl(
        `/requests/${params.row.uuid}`
      );
    }
  };

  if (isLiscioLite) {
    return null;
  }

  return (
    <>
      <Header
        title="My Requests"
        // Line the title up with the Messages tab labels (16px tab padding).
        sx={{ py: 0, pl: 2, pr: 0 }}
        isLoading={isLoading}
        numberOfItems={totalCount}
        onSeeAllClick={() => {
          navigate(`/${REQUESTS_INDEX}${REQUESTS_PATHS.list}`);
        }}
      />
      <Box sx={{ width: '100%' }}>
        {error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              Unable to load your requests. Please try again.
            </Alert>
          </Box>
        ) : isSuccess && rows.length === 0 ? (
          <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
            <Typography variant="body1">
              No open requests assigned to you.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Assigned requests will appear here.
            </Typography>
          </Box>
        ) : (
          <MyRequestsDataGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.uuid}
            disableColumnMenu
            disableRowSelectionOnClick
            hideFooter
            columnHeaderHeight={36}
            rowHeight={52}
            onRowClick={handleRowClick}
            autoHeight
          />
        )}
      </Box>
    </>
  );
};

export default DashboardMyRequests;
